import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { showToast } from '../components/ui/toast.utils';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import {
  createNonconformingOutput,
  escalateNonconformingOutput,
  fetchNonconformingOutputs,
  transitionNonconformingOutputStatus,
} from '../features/nonconforming-outputs/nonconformingOutputsApi';
import type {
  NonconformingDisposition,
  NonconformingOutputSummary,
} from '../features/nonconforming-outputs/types';
import {
  DISPOSITION_LABEL,
  NEXT_STATUS,
  STATUS_LABEL,
  statusBadgeClass,
} from '../features/nonconforming-outputs/nonconformingUtils';

export function NonconformingOutputsPage() {
  const { canEdit } = usePlatformAccessLevel('nonconforming_output.access');
  const [items, setItems] = useState<NonconformingOutputSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [productIdentification, setProductIdentification] = useState('');
  const [batchLot, setBatchLot] = useState('');
  const [description, setDescription] = useState('');
  const [transitionItem, setTransitionItem] = useState<NonconformingOutputSummary | null>(null);
  const [transitionDisposition, setTransitionDisposition] =
    useState<NonconformingDisposition>('reproceso');
  const [transitionEvidenceNote, setTransitionEvidenceNote] = useState('');
  const [transitionSaving, setTransitionSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchNonconformingOutputs({
        page,
        pageSize: 20,
        search: search.trim() || undefined,
      });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar las salidas no conformes.');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    if (!productIdentification.trim() || !description.trim()) return;
    await createNonconformingOutput({
      productIdentification,
      batchLot: batchLot || null,
      description,
    });
    setCreateOpen(false);
    setProductIdentification('');
    setBatchLot('');
    setDescription('');
    await load();
  };

  const advanceStatus = async (item: NonconformingOutputSummary) => {
    const next = NEXT_STATUS[item.status];
    if (!next) return;

    setTransitionItem(item);
    setTransitionDisposition(item.disposition ?? 'reproceso');
    setTransitionEvidenceNote(item.evidenceNote ?? '');
  };

  const closeTransitionDialog = () => {
    if (transitionSaving) return;
    setTransitionItem(null);
    setTransitionDisposition('reproceso');
    setTransitionEvidenceNote('');
  };

  const submitTransition = async () => {
    if (!transitionItem) return;
    const next = NEXT_STATUS[transitionItem.status];
    if (!next) return;

    setTransitionSaving(true);
    try {
      if (next === 'en_disposicion') {
        await transitionNonconformingOutputStatus(transitionItem.id, {
          status: next,
          disposition: transitionDisposition,
        });
      } else {
        const evidenceNote = transitionEvidenceNote.trim();
        if (!evidenceNote) {
          showToast('La evidencia de cierre es obligatoria.', 'error');
          return;
        }
        await transitionNonconformingOutputStatus(transitionItem.id, {
          status: next,
          evidenceNote,
        });
      }
      await load();
      showToast('Estado actualizado.', 'success');
      closeTransitionDialog();
    } catch {
      showToast('No se pudo actualizar el estado.', 'error');
    } finally {
      setTransitionSaving(false);
    }
  };

  const escalate = async (item: NonconformingOutputSummary) => {
    await escalateNonconformingOutput(item.id);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Salidas no conformes</h1>
          <p className="text-muted-foreground text-sm">
            ISO 9001 — trazabilidad de producto/lote y disposición
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            Registrar salida
          </button>
        )}
      </div>

      <input
        type="search"
        placeholder="Buscar por código, producto o lote…"
        className="rounded-md border border-border px-3 py-2 text-sm min-w-[200px]"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {loading && <p className="text-muted-foreground text-sm">Cargando…</p>}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Código</th>
              <th className="px-3 py-2 text-left">Producto / lote</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Disposición</th>
              <th className="px-3 py-2 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{item.code}</td>
                <td className="px-3 py-2">
                  <div>{item.productIdentification}</div>
                  {item.batchLot && (
                    <div className="text-xs text-muted-foreground">Lote: {item.batchLot}</div>
                  )}
                  <div className="text-xs text-muted-foreground line-clamp-2">{item.description}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${statusBadgeClass(item.status)}`}>
                    {STATUS_LABEL[item.status]}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {item.disposition ? DISPOSITION_LABEL[item.disposition] : '—'}
                </td>
                <td className="px-3 py-2 space-x-2">
                  {canEdit && NEXT_STATUS[item.status] && (
                    <button
                      type="button"
                      className="text-primary underline text-xs"
                      onClick={() => void advanceStatus(item)}
                    >
                      Avanzar
                    </button>
                  )}
                  {item.bridgeRecordId ? (
                    <Link
                      className="text-primary underline text-xs"
                      to={`/registros/no-conformidades/${item.bridgeRecordId}`}
                    >
                      Ver NC
                    </Link>
                  ) : (
                    canEdit && (
                      <button
                        type="button"
                        className="text-primary underline text-xs"
                        onClick={() => void escalate(item)}
                      >
                        CAPA
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm self-center">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background rounded-lg border border-border p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-semibold">Registrar salida no conforme</h3>
            <input
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
              placeholder="Identificación del producto"
              value={productIdentification}
              onChange={(e) => setProductIdentification(e.target.value)}
            />
            <input
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
              placeholder="Lote (opcional)"
              value={batchLot}
              onChange={(e) => setBatchLot(e.target.value)}
            />
            <textarea
              className="w-full rounded-md border border-border px-3 py-2 text-sm min-h-[100px]"
              placeholder="Descripción del no conforme"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-outline" onClick={() => setCreateOpen(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void handleCreate()}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={Boolean(transitionItem)} onOpenChange={(open) => !open && closeTransitionDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Avanzar salida no conforme</DialogTitle>
            <DialogDescription>
              {transitionItem ? `Actualizar ${transitionItem.code} a ${STATUS_LABEL[NEXT_STATUS[transitionItem.status]!].toLowerCase()}.` : ''}
            </DialogDescription>
          </DialogHeader>

          {transitionItem && NEXT_STATUS[transitionItem.status] === 'en_disposicion' ? (
            <label className="space-y-2 text-sm">
              <span className="font-medium">Disposición</span>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={transitionDisposition}
                onChange={(e) =>
                  setTransitionDisposition(e.target.value as NonconformingDisposition)
                }
                disabled={transitionSaving}
              >
                {Object.entries(DISPOSITION_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="space-y-2 text-sm">
              <span className="font-medium">Evidencia de cierre</span>
              <textarea
                className="min-h-[120px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Describí la evidencia que respalda el cierre"
                value={transitionEvidenceNote}
                onChange={(e) => setTransitionEvidenceNote(e.target.value)}
                disabled={transitionSaving}
              />
            </label>
          )}

          <DialogFooter>
            <button
              type="button"
              className="btn btn-outline"
              onClick={closeTransitionDialog}
              disabled={transitionSaving}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void submitTransition()}
              disabled={
                transitionSaving ||
                (transitionItem !== null &&
                  NEXT_STATUS[transitionItem.status] === 'cerrado' &&
                  !transitionEvidenceNote.trim())
              }
            >
              {transitionSaving ? 'Guardando…' : 'Confirmar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
